import L from "leaflet";
import { getRadarEnergyInfrastructureItems, type GermanEnergyInfrastructureResponse } from "@shared/germanEnergyInfrastructure";
import { supplementalInfrastructureAsGeozones, infrastructureMapGeometry } from "@client/lib/energyInfrastructureZones";
import type { GeozoneFeatureInfo } from "@shared/geozoneFeatureInfo";
import { buildEd269PopupHtml } from "./ed269Popup";

export function energyZonesForResponse(data: GermanEnergyInfrastructureResponse): GeozoneFeatureInfo[] {
  const verified = getRadarEnergyInfrastructureItems(true, data.features, data.sources, data.country.assetComparisonStatusKeyByKind);
  const items = data.country.iso === "DE"
    ? [...data.features.filter((item) => item.source === "MaStR"), ...verified.filter((item) => item.source !== "MaStR")]
    : verified;
  return supplementalInfrastructureAsGeozones(data.country.iso, items);
}

export function infrastructureZoneFeature(zone: GeozoneFeatureInfo): GeoJSON.Feature {
  const geometry = zone.infrastructureGeometry ?? {
    type: "MultiPolygon",
    coordinates: (zone.polygonGroups ?? []).map((group) => group.map((ring) => ring.map(([lat, lon]) => [lon, lat]))),
  };
  return {
    type: "Feature",
    geometry: geometry as GeoJSON.Geometry,
    properties: {
      name: zone.name, country: zone.country, restriction: zone.restriction,
      informationalOnly: zone.informationalOnly, message: zone.details,
      applicationText: zone.applicationText,
    },
  };
}

/** Same geometry styles and popup builder as normal vector geozones. */
export function renderEnergyInfrastructureLayer(
  layer: L.LayerGroup,
  data: GermanEnergyInfrastructureResponse,
  onFocus?: (feature: GeoJSON.Feature) => void,
): void {
  for (const zone of energyZonesForResponse(data)) {
    const geometry = infrastructureMapGeometry(zone.infrastructureGeometry);
    const polygons = zone.polygonGroups ?? geometry.polygons;
    const shape = L.layerGroup();
    const style = { color: zone.strokeColor, weight: 2, opacity: 0.95, fillColor: zone.fillColor, fillOpacity: 1 };
    for (const rings of polygons) L.polygon(rings, style).addTo(shape);
    for (const point of geometry.points) L.circleMarker(point, {
      color: zone.strokeColor, weight: 2, radius: 5, fillColor: zone.strokeColor, fillOpacity: 0.4,
    }).addTo(shape);
    for (const line of geometry.lines) L.polyline(line, style).addTo(shape);
    const html = buildEd269PopupHtml({
      name: zone.name, sourceLabel: "",
      restrictionLabel: zone.informationalOnly ? "Infrastrukturhinweis; örtliche Regeln maßgeblich" : "Zustimmungspflicht unter § 21h Abs. 3 Nr. 3 LuftVO",
      altitude: zone.informationalOnly ? "Keine Luftraumgrenze angegeben" : "0 m AGL",
      detail: zone.details, appText: zone.applicationText, links: zone.links,
      focusButtonLabel: onFocus ? "Nur diese Anlage anzeigen" : undefined,
    });
    shape.eachLayer((entry) => {
      entry.bindPopup(html);
      if (onFocus) entry.on("popupopen", (event: L.PopupEvent) => {
        const button = event.popup.getElement()?.querySelector("[data-ed269-focus]");
        button?.addEventListener("click", () => {
          entry.closePopup();
          onFocus(infrastructureZoneFeature(zone));
        }, { once: true });
      });
    });
    shape.addTo(layer);
  }
}
